import JsBarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import type { CodeDisplayMode } from './BarcodeLabelPreview';

export interface BarcodeLabelData {
  /** Barcode value string. */
  value: string;
  /** Product display name including unit, e.g. "Rice (1kg)". */
  productName: string;
  /** Formatted price string, e.g. "150.00 MZN". */
  price?: string;
  /** When sold by range, formatted range string, e.g. "100–200 MZN". */
  priceRange?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function barcodeToDataUrl(value: string, width = 1.5, height = 30): string {
  const canvas = document.createElement('canvas');
  try {
    JsBarcode(canvas, value, {
      format: 'CODE128',
      width,
      height,
      displayValue: false,
      margin: 0,
    });
  } catch {
    JsBarcode(canvas, value, {
      format: 'CODE128',
      width,
      height,
      displayValue: false,
      margin: 0,
      valid: () => true,
    });
  }
  return canvas.toDataURL('image/png');
}

async function qrCodeToDataUrl(value: string, size = 150): Promise<string> {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, value, {
    width: size,
    margin: 1,
    errorCorrectionLevel: 'M',
  });
  return canvas.toDataURL('image/png');
}

function wrapText(doc: jsPDF, text: string, maxWidth: number): string[] {
  return doc.splitTextToSize(text, maxWidth) as string[];
}

// ─── Label dimensions (mm) ─────────────────────────────────────────────────────
const BC_LABEL_W = 60;
const BC_LABEL_H = 40;
const QR_LABEL_W = 40;
const QR_LABEL_H = 60;
const PAD = 1.5;
const BC_CONTENT_W = BC_LABEL_W - PAD * 2;
const QR_CONTENT_W = QR_LABEL_W - PAD * 2;

export async function generateBarcodeLabelPdf(
  labels: BarcodeLabelData[],
  includePrice: boolean,
  codeMode: CodeDisplayMode = 'qrcode',
): Promise<jsPDF> {
  const isQR = codeMode === 'qrcode';
  const labelW = isQR ? QR_LABEL_W : BC_LABEL_W;
  const labelH = isQR ? QR_LABEL_H : BC_LABEL_H;
  const doc = new jsPDF({
    orientation: isQR ? 'portrait' : 'landscape',
    unit: 'mm',
    format: [labelW, labelH],
  });

  for (let idx = 0; idx < labels.length; idx++) {
    if (idx > 0) doc.addPage([labelW, labelH], isQR ? 'portrait' : 'landscape');
    await drawLabel(doc, labels[idx], includePrice, codeMode, labelW, labelH);
  }

  return doc;
}

async function drawLabel(
  doc: jsPDF,
  label: BarcodeLabelData,
  includePrice: boolean,
  codeMode: CodeDisplayMode,
  labelW: number,
  labelH: number,
): Promise<void> {
  const isQR = codeMode === 'qrcode';
  const contentW = isQR ? QR_CONTENT_W : BC_CONTENT_W;
  let y = PAD;

  doc.setFontSize(isQR ? 7.5 : 7);
  doc.setFont('helvetica', 'bold');
  const nameLines = wrapText(doc, label.productName, contentW);
  const nameLinesUsed = nameLines.slice(0, 2);
  nameLinesUsed.forEach((line) => {
    y += 2.5;
    doc.text(line, labelW / 2, y, { align: 'center' });
  });

  if (includePrice) {
    const priceStr = label.priceRange ?? label.price ?? '';
    if (priceStr) {
      y += 2.5;
      doc.setFontSize(isQR ? 6.5 : 6);
      doc.setFont('helvetica', 'normal');
      doc.text(priceStr, labelW / 2, y, { align: 'center' });
    }
  }

  y += 1;

  if (isQR) {
    const qrDataUrl = await qrCodeToDataUrl(label.value, 300);
    const available = labelH - y - 7;
    const qrSize = Math.min(available, contentW);
    const qrX = (labelW - qrSize) / 2;
    doc.addImage(qrDataUrl, 'PNG', qrX, y, qrSize, qrSize);
    y += qrSize + 0.5;
  } else {
    const barcodeDataUrl = barcodeToDataUrl(label.value, 2, 40);
    const barcodeImgW = contentW - 2;
    const available = labelH - y - 7;
    const barcodeImgH = Math.min(available, 18);
    const barcodeX = (labelW - barcodeImgW) / 2;
    doc.addImage(barcodeDataUrl, 'PNG', barcodeX, y, barcodeImgW, barcodeImgH);
    y += barcodeImgH + 0.5;
  }

  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.text(String(label.value), labelW / 2, y + 1.5, { align: 'center' });

  doc.setFontSize(5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text('www.shopnsmile.org', labelW / 2, labelH - PAD, { align: 'center' });
  doc.setTextColor(0, 0, 0);
}

export async function downloadBarcodeLabelPdf(
  labels: BarcodeLabelData[],
  includePrice: boolean,
  filename = 'barcode-labels.pdf',
  codeMode: CodeDisplayMode = 'qrcode',
): Promise<void> {
  const doc = await generateBarcodeLabelPdf(labels, includePrice, codeMode);
  doc.save(filename);
}
